import { useEffect, useMemo, useRef, useState } from "react";
import { feedItems } from "@/mocks/feed";

const links = [
  { href: "#breaking", label: "前线在线" },
  { href: "#hot", label: "今日热榜" },
  { href: "#feed", label: "前线爆料" },
  { href: "#chain", label: "事件链条" },
  { href: "#faq", label: "前线问答" },
  { href: "#about", label: "关于我们" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (searchOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [searchOpen]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return feedItems.slice(0, 5);
    return feedItems.filter((f) => f.title.toLowerCase().includes(q) || f.tags.join("").toLowerCase().includes(q)).slice(0, 6);
  }, [query]);

  const jump = (id: string) => {
    setSearchOpen(false);
    setMenuOpen(false);
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 40);
  };

  return (
    <>
      <header
        className={`sticky top-0 z-50 w-full transition-all duration-300 ${
          scrolled ? "border-b border-background-200/70 bg-background-50/90 backdrop-blur-md" : "bg-transparent"
        }`}
      >
        <nav className="mx-auto flex w-full max-w-[1200px] items-center justify-between gap-4 px-4 py-3 md:px-6" aria-label="主导航">
          <a href="#top" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-2.5 whitespace-nowrap cursor-pointer">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500 text-background-50">
              <i className="ri-eye-2-fill text-xl" aria-hidden="true"></i>
            </span>
            <span className="leading-tight">
              <span className="block font-heading text-lg font-extrabold tracking-tight text-foreground-950">
                吃瓜前线
              </span>
              <span className="font-script block text-[11px] leading-none text-primary-600">GUA · FRONTLINE</span>
            </span>
          </a>

          <div className="hidden items-center gap-1 lg:flex">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => {
                  e.preventDefault();
                  jump(l.href.replace("#", ""));
                }}
                className="rounded-md px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-foreground-950 whitespace-nowrap cursor-pointer"
              >
                {l.label}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSearchOpen((v) => !v)}
              aria-label="搜索前线内容"
              className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-secondary-100 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-search-line text-xl" aria-hidden="true"></i>
            </button>
            <a
              href="#about"
              onClick={(e) => {
                e.preventDefault();
                jump("about");
              }}
              className="hidden items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 sm:inline-flex whitespace-nowrap cursor-pointer"
            >
              <i className="ri-send-plane-fill" aria-hidden="true"></i>
              我要爆料
            </a>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={menuOpen ? "关闭菜单" : "打开菜单"}
              aria-expanded={menuOpen}
              className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-800 hover:bg-secondary-100 lg:hidden cursor-pointer whitespace-nowrap"
            >
              <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} aria-hidden="true"></i>
            </button>
          </div>
        </nav>

        {/* mobile menu */}
        <div
          className={`overflow-hidden border-t border-background-200/70 bg-background-50 transition-[max-height] duration-300 lg:hidden ${
            menuOpen ? "max-h-[400px]" : "max-h-0 border-t-0"
          }`}
        >
          <div className="mx-auto max-w-[1200px] px-4 py-3 md:px-6">
            <div className="grid grid-cols-2 gap-1">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => {
                    e.preventDefault();
                    jump(l.href.replace("#", ""));
                  }}
                  className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-secondary-100 cursor-pointer"
                >
                  {l.label}
                </a>
              ))}
            </div>
            <a
              href="#about"
              onClick={(e) => {
                e.preventDefault();
                jump("about");
              }}
              className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-send-plane-fill" aria-hidden="true"></i>
              我要爆料
            </a>
          </div>
        </div>
      </header>

      {/* search overlay */}
      {searchOpen ? (
        <div className="fixed inset-x-0 top-[60px] z-[60] bg-background-50/95 px-4 py-6 backdrop-blur-md md:top-[66px] md:px-6">
          <div className="mx-auto max-w-[720px]">
            <div className="flex items-center gap-2 rounded-lg border border-background-300 bg-background-50 px-4 py-3 focus-within:border-primary-400">
              <i className="ri-search-line text-xl text-foreground-500" aria-hidden="true"></i>
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜事件、热梗或栏目，例如：路透、翻车、夜市"
                className="w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-500"
              />
              {query ? (
                <button type="button" onClick={() => setQuery("")} aria-label="清空" className="text-foreground-500 hover:text-foreground-900 cursor-pointer">
                  <i className="ri-close-circle-fill text-lg" aria-hidden="true"></i>
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  setSearchOpen(false);
                  jump("feed");
                }}
                className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-1.5 text-sm font-medium text-background-50 hover:bg-primary-600 cursor-pointer"
              >
                全网搜
              </button>
            </div>
            <div className="mt-4">
              <p className="mb-2 px-1 text-xs font-medium uppercase tracking-wide text-foreground-500">
                {query ? "搜索结果" : "前线热榜直达"}
              </p>
              <ul className="grid gap-1 sm:grid-cols-2">
                {results.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setQuery(r.title);
                        jump("feed");
                      }}
                      className="w-full rounded-lg px-3 py-2 text-left text-sm text-foreground-800 transition-colors hover:bg-secondary-100 cursor-pointer"
                    >
                      <span className="line-clamp-1">{r.title}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}